import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';

export interface SelectOption {
  /** No puede ser cadena vacía (Radix lo prohíbe): usa un valor centinela como "todos". */
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

/** Lista desplegable accesible (Radix): teclado completo, búsqueda por letra, 44 px de alto. */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    value,
    defaultValue,
    onValueChange,
    placeholder,
    id,
    name,
    disabled,
    invalid,
    className,
    ...aria
  },
  ref,
) {
  return (
    <SelectPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      name={name}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        aria-invalid={invalid || undefined}
        data-invalid={invalid ? 'true' : undefined}
        className={cn(
          'flex h-11 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-left text-base text-fg',
          'transition-[border-color] duration-fast ease-standard data-[invalid=true]:border-down',
          'disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60 data-[placeholder]:text-fg-muted',
          className,
        )}
        {...aria}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <Icon icon={ChevronDown} size={16} className="text-fg-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className="anim-popover surface-float z-50 max-h-[min(20rem,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="relative flex h-11 cursor-default select-none items-center rounded-sm pl-8 pr-3 text-base text-fg outline-none data-[highlighted]:bg-primary-tint data-[state=checked]:font-semibold data-[disabled]:opacity-50"
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2 text-primary">
                  <Icon icon={Check} size={16} />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
